import type { FC, JSX } from 'react';
import { Table as ReactTable, flexRender } from '@tanstack/react-table';

type TableProps = {
  isFetching: boolean;
  table: ReactTable<any>;
  classes?: string;
  // eslint-disable-next-line no-unused-vars
  filters?: (header: any) => JSX.Element;
};

/**
 * @description Determines the CSS class for sorting based on the column's sort direction
 *
 * @SADtrace SAD-003-2.1, SAD-003-3.1, SAD-003-4.1, SAD-003-5.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {any} header - The header object from TanStack Table
 *
 * @returns {string} The CSS class name for the sort direction
 */
const getSortClass = (header: any) => {
  const ascDescClasses: { [key: string]: string } = {
    asc: 'dl-pagination__asc',
    desc: 'dl-pagination__desc',
  };

  return ascDescClasses[header.column.getIsSorted() as string] ?? '';
};

/**
 * @description A reusable table component built on top of TanStack Table.
 * Provides sorting, filtering, and column visibility features for data display.
 *
 * @SADtrace SAD-003-2.1, SAD-003-3.1, SAD-003-4.1, SAD-003-5.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {object} props - Component props
 * @param {ReactTable<any>} props.table - The TanStack table instance
 * @param {boolean} props.isFetching - Whether data is currently being fetched
 * @param {(header: any) => JSX.Element} [props.filters] - Optional function to render filter components for columns
 * @param {string} [props.classes] - Optional CSS classes to apply to the table wrapper
 *
 * @returns {JSX.Element} The rendered table component
 */
const Table: FC<TableProps> = ({ table, isFetching, filters, classes }) => {
  return (
    <div className={`table-wrapper ${classes ? classes : ''}`}>
      <table className={'table table-dark ' + (isFetching ? 'opacity-50' : '')}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const lastHeaderInTreeBranch = header.subHeaders.length === 0;

                return (
                  <th
                    key={header.id}
                    data-testid={
                      lastHeaderInTreeBranch && !header.isPlaceholder
                        ? `${header.id} columnHeader`
                        : ''
                    }
                    colSpan={header.colSpan}
                    className={(header.column.columnDef as any).className}
                  >
                    {header.isPlaceholder ? (
                      ''
                    ) : (
                      <>
                        <div
                          {...{
                            className:
                              (header.column.getCanSort()
                                ? 'table-header dl-pagination__header cursor-pointer select-none '
                                : '') + getSortClass(header),
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </div>
                        {header.column.getCanFilter() ? (
                          <div className="p-2">
                            {filters && filters(header)}
                          </div>
                        ) : (
                          ''
                        )}
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => {
                return (
                  <td
                    key={cell.id}
                    className={(cell.column.columnDef as any).className}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.footer,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>
    </div>
  );
};

export default Table;
